import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, X, ChevronDown, ChevronRight, AlignJustify } from "lucide-react";
import { useAuth } from "../../../utils/AuthContext ";
import BulkOrder from "../../Order/BulkOrder";

const BottomNav = () => {
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [showAllCategories, setShowAllCategories] = useState(false);
  const [hoveredCategory, setHoveredCategory] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isBulkOrderOpen, setIsBulkOrderOpen] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);

        const categoriesResponse = await fetch(
          `${process.env.REACT_APP_API_URL}/categories/get-all-categories`
        );
        if (!categoriesResponse.ok)
          throw new Error("Failed to fetch categories");
        const categoriesData = await categoriesResponse.json();

        const subCategoriesResponse = await fetch(
          `${process.env.REACT_APP_API_URL}/sub-categories/get-all-sub-categories`
        );
        if (!subCategoriesResponse.ok)
          throw new Error("Failed to fetch subcategories");
        const subCategoriesData = await subCategoriesResponse.json();

        setCategories(categoriesData);
        setSubCategories(subCategoriesData);
        setIsLoading(false);
      } catch (err) {
        setError("Failed to fetch data");
        setIsLoading(false);
        console.error(err);
      }
    };

    fetchData();
  }, []);

  const getSubCategoriesForCategory = (categoryId) => {
    return subCategories.filter(
      (subCat) => subCat?.category?._id === categoryId
    );
  };

  const handleSubCategoryClick = (categoryId, subCategoryId) => {
    setShowAllCategories(false);
    setIsSidebarOpen(false);
    navigate(`/allcategories/${categoryId}`);
  };

  const handleAllCategoriesHover = () => {
    setShowAllCategories(true);
  };

  const handleAllCategoriesLeave = () => {
    setShowAllCategories(false);
    setHoveredCategory(null);
  };

  const handleCategoryHover = (categoryId) => {
    setHoveredCategory(categoryId);
  };

  const handleBulkOrderClick = (e) => {
    e.preventDefault();
    setIsBulkOrderOpen(true);
  };

  const handleCloseBulkOrder = () => {
    setIsBulkOrderOpen(false);
  };

  if (isLoading) {
    return (
      <React.Fragment>
        <div className="sm:hidden bg-white shadow-sm p-4">
          <div className="animate-pulse space-y-3">
            {[...Array(1)].map((_, i) => (
              <div key={i} className="h-4 bg-gray-200 rounded w-2/3"></div>
            ))}
          </div>
        </div>

        <nav className="hidden sm:block bg-white shadow-sm py-1">
          <div className="mx-auto px-4 sm:px-6 lg:px-8 py-3">
            <div className="animate-pulse flex space-x-6 justify-center">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="h-5 w-28 bg-gray-200 rounded"></div>
              ))}
            </div>
          </div>
        </nav>
      </React.Fragment>
    );
  }

  if (error) {
    return (
      <div className="w-full bg-white p-4 text-center text-red-500">
        {error}
      </div>
    );
  }

  return (
    <>
      <div className="bg-white border-b border-gray-100 shadow-sm py-">
        {user && (
          <div className="block sm:hidden px-4 pb-3">
            <div className="flex items-center justify-center">
              <div className="flex items-center bg-gradient-to-r from-teal-50 to-teal-100 px-4 py-2 rounded-lg border border-teal-200 shadow-sm w-full justify-center">
                <div className="flex items-center space-x-2">
                  <div>
                    <span className="text-sm text-teal-600 font-medium">
                      Welcome,
                    </span>
                    <span className="text-sm font-semibold text-gray-800 capitalize ml-1">
                      {user.firstName}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        <nav className="hidden sm:block">
          <div className="flex justify-between items-center">
            <div className="px-4 sm:px-6 lg:px-8">
              <div className="flex justify-between items-center">
                <div
                  className="relative"
                  onMouseEnter={handleAllCategoriesHover}
                  onMouseLeave={handleAllCategoriesLeave}
                >
                  <button className="capitalize px-4 py-3 text-white bg-[#00A2A2] hover:bg-teal-600 font-medium text-sm xl:text-[15px] flex items-center space-x-2 transition-colors duration-200 rounded">
                    <AlignJustify size={22} />
                    <span>All Categories</span>
                    <ChevronDown
                      className={`w-4 h-4 text-white transform transition-transform ${
                        showAllCategories ? "rotate-180" : ""
                      }`}
                    />
                  </button>

                  {showAllCategories && (
                    <div className="absolute left-0 top-full z-10 flex items-start">
                      <div className="w-64 bg-white rounded-lg shadow-lg ring-1 ring-black ring-opacity-5 overflow-hidden">
                        <ul>
                          {categories.map((category) => (
                            <li
                              key={category?._id}
                              className="border-b border-gray-100 last:border-0 relative"
                              onMouseEnter={() =>
                                handleCategoryHover(category?._id)
                              }
                            >
                              <button className="px-4 py-3 text-gray-700 hover:bg-gray-50 w-full text-left flex items-center justify-between">
                                <span className="text-sm capitalize font-medium">
                                  {category.name}
                                </span>
                                <ChevronRight className="w-4 h-4 text-gray-400" />
                              </button>
                            </li>
                          ))}
                        </ul>
                      </div>

                      {hoveredCategory && (
                        <div className="w-72 bg-white rounded-lg shadow-lg ring-1 ring-black ring-opacity-5 overflow-hidden ml-1 min-h-0 h-auto">
                          <div className="flex-1 min-h-0">
                            <ul className="max-h-80 overflow-y-auto">
                              {getSubCategoriesForCategory(hoveredCategory).map(
                                (subCategory) => (
                                  <li
                                    key={subCategory?._id}
                                    className="border-b border-gray-100 last:border-0"
                                  >
                                    <button
                                      onClick={() =>
                                        handleSubCategoryClick(
                                          hoveredCategory,
                                          subCategory?._id
                                        )
                                      }
                                      className="px-4 py-2 text-gray-700 hover:bg-gray-50 w-full text-left flex items-center"
                                    >
                                      {subCategory.imgUrl && (
                                        <div className="flex-shrink-0 h-6 w-6 rounded-md overflow-hidden bg-gray-100 mr-3">
                                          <img
                                            src={subCategory.imgUrl}
                                            alt={subCategory.name}
                                            className="h-full w-full object-cover"
                                          />
                                        </div>
                                      )}
                                      <span className="truncate text-sm font-medium capitalize ">
                                        {subCategory.name}
                                      </span>
                                    </button>
                                  </li>
                                )
                              )}
                              {getSubCategoriesForCategory(hoveredCategory)
                                .length === 0 && (
                                <li className="px-4 py-3 text-gray-500 text-sm">
                                  No subcategories available
                                </li>
                              )}
                            </ul>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div className="hidden lg:block">
                  <Link
                    to="/"
                    className="capitalize px-4 py-3 text-gray-700 hover:text-blue-600 lg:text-sm xl:text-[15px] transition-colors duration-200 font-medium"
                  >
                    Home
                  </Link>
                </div>
                <div className="hidden lg:block">
                  <Link
                    to="/about"
                    className="capitalize px-4 py-3 text-gray-700 hover:text-blue-600 font-medium lg:text-sm xl:text-[15px] transition-colors duration-200"
                  >
                    About
                  </Link>
                </div>
                <div className="hidden lg:block">
                  <Link
                    to="/display-house"
                    className="capitalize px-4 py-3 text-gray-700 hover:text-blue-600 font-medium lg:text-sm xl:text-[15px] transition-colors duration-200"
                  >
                    Display House
                  </Link>
                </div>
                <div className="hidden lg:block">
                  <Link
                    to="/career"
                    className="capitalize px-4 py-3 text-gray-700 hover:text-blue-600 font-medium lg:text-sm xl:text-[15px] transition-colors duration-200"
                  >
                    Career
                  </Link>
                </div>
                <div className="hidden lg:block">
                  <button
                    onClick={handleBulkOrderClick}
                    className="capitalize px-4 py-3 text-gray-700 hover:text-blue-600 font-medium lg:text-sm xl:text-[15px] transition-colors duration-200"
                  >
                    Bulk Order
                  </button>
                </div>
              </div>
            </div>

            <div className="px-4 sm:px-6 lg:px-8">
              {user && (
                <div className="flex flex-col gap-1 py-2 rounded-lg shadow-sm lg:items-end">
                  <div className="flex items-center">
                    <p className="lg:text-sm xl:text-[15px] text-orange-600 font-medium mr-1">
                      Welcome,
                    </p>
                    <p className="text-sm xl:text-[15px] font-semibold text-gray-800 capitalize">
                      {user.firstName}
                    </p>
                  </div>
                  <p className="text-sm xl:text-[15px] font-semibold text-gray-800 capitalize">
                    {user.phone}
                  </p>
                </div>
              )}
            </div>
          </div>
        </nav>
      </div>

      <BulkOrder isOpen={isBulkOrderOpen} onClose={handleCloseBulkOrder} />
    </>
  );
};

export default BottomNav;
